import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FeatureDemoScaleFit } from '~/landing_page/components/FeaturesSection/FeatureDemoScaleFit';
import { usePrefersReducedMotion } from '~/landing_page/components/FeaturesSection/usePrefersReducedMotion';
import {
  ResearchWorkflowWidgetUI,
  type ResearchResultItem,
  type ResearchSectionItem,
  type ResearchWorkflowWidgetUIProps,
} from '~/design-system/components/PlanningChat/ui/ResearchWorkflowWidgetUI';
import { PlaygroundChatBar } from './PlaygroundChatBar';
import styles from './FeaturePlaygroundExperiment.module.css';

// Exact Figma node 3651-9546 (RightContainer / feature visual).
const DESIGN_WIDTH = 682;
const DESIGN_HEIGHT = 580;

// Cursor rest position (lower right, near the edge) in design coordinates.
const REST = { x: 600, y: 470 };
// How far the camera zooms into the bar for the typing scene. Keeps margin on both sides.
const ZOOM = 1.4;
// The query that gets typed once search is on.
const TYPED_TEXT = 'How is the storytelling market performing in 2026?';
const CHAR_MS = 25;

// Cursor springs stay snappy. The camera zoom is a smooth ease, not a spring, so scaling down into the
// search reads as one calm move instead of a rigid snap.
const SPRING_MOVE = { type: 'spring', stiffness: 300, damping: 32 } as const;
const SPRING_PRESS = { type: 'spring', stiffness: 600, damping: 36 } as const;
const ZOOM_T = { duration: 0.7, ease: [0.4, 0, 0.2, 1] } as const;
// The bar and the results re-centre as a group when sources arrive; this glides them into place.
const POS_T = { duration: 0.42, ease: [0.4, 0, 0.2, 1] } as const;

// 'idle' = no search. 'searching' = shimmer only. 'found1'/'found2' = one/two searches' sources shown.
type SearchPhase = 'idle' | 'searching' | 'found1' | 'found2';

const mkResults = (pairs: [string, string][], prefix: string): ResearchResultItem[] =>
  pairs.map(([title, domain], i) => ({ id: `${prefix}${i}`, title, domain }));

// Each search returns ~20 sources. The widget's result panel is fixed height and scrollable, so it
// shows about three and scrolls through the rest, which keeps the card a predictable size.
const SECTION_1: ResearchSectionItem = {
  id: 'q1',
  query: 'How is the storytelling market performing in 2026',
  resultCount: 20,
  status: 'complete',
  results: mkResults(
    [
      ['The State of Storytelling 2026', 'hbr.org'],
      ['Narrative tools market size and growth', 'www.gartner.com'],
      ['Why storytelling drives revenue in 2026', 'www.forbes.com'],
      ['Storytelling platforms compared', 'techcrunch.com'],
      ['B2B storytelling trends report', 'www.mckinsey.com'],
      ['The rise of visual storytelling', 'www.theverge.com'],
      ['Storytelling market forecast 2026 to 2030', 'www.statista.com'],
      ['How brands win with story', 'www.fastcompany.com'],
      ['Storytelling and buyer decisions', 'www.cmswire.com'],
      ['Content storytelling benchmarks', 'contentmarketinginstitute.com'],
      ['Narrative design in product', 'www.nngroup.com'],
      ['Storytelling ROI study 2026', 'www.hubspot.com'],
      ['The data behind great stories', 'www.thinkwithgoogle.com'],
      ['Storytelling for startups', 'www.ycombinator.com'],
      ['Visual narrative tools roundup', 'www.producthunt.com'],
      ['Storytelling in the AI era', 'www.wired.com'],
      ['Market map: storytelling software', 'www.cbinsights.com'],
      ['The economics of attention', 'www.economist.com'],
      ['Storytelling skills gap 2026', 'www.linkedin.com'],
      ['Storytelling adoption survey', 'www.deloitte.com'],
    ],
    'a',
  ),
};
const SECTION_2: ResearchSectionItem = {
  id: 'q2',
  query: 'Top storytelling platforms and tools, 2026',
  resultCount: 20,
  status: 'complete',
  results: mkResults(
    [
      ['Best storytelling platforms for teams', 'techcrunch.com'],
      ['Storytelling tools compared, 2026', 'www.theverge.com'],
      ['Top 10 narrative tools this year', 'www.producthunt.com'],
      ['Presentation tools market', 'www.capterra.com'],
      ['Story builders for product teams', 'www.nngroup.com'],
      ['Visual storytelling apps ranked', 'www.fastcompany.com'],
      ['The new wave of deck tools', 'www.wired.com'],
      ['Storytelling software reviews', 'www.trustradius.com'],
      ['Tools that turn data into story', 'www.thinkwithgoogle.com'],
      ['Pitch and narrative platforms', 'www.crunchbase.com'],
      ['Best tools for sales storytelling', 'www.salesforce.com'],
      ['The storytelling stack for 2026', 'www.notion.so'],
      ['Collaborative story tools', 'www.figma.com'],
      ['AI storytelling assistants', 'www.openai.com'],
      ['Narrative analytics platforms', 'www.amplitude.com'],
      ['Story-first design tools', 'www.smashingmagazine.com'],
      ['Tools for async storytelling', 'www.loom.com'],
      ['Storytelling tool buyer guide', 'www.gartner.com'],
      ['Best decks for fundraising', 'www.crunchbase.com'],
      ['The story tooling landscape', 'a16z.com'],
    ],
    'b',
  ),
};

// Header keeps shimmering "Searching the web" the whole time (it searches more than once), sources
// stream in below. No Done row.
const SEARCH_PROPS: Record<Exclude<SearchPhase, 'idle'>, ResearchWorkflowWidgetUIProps> = {
  searching: { mode: 'web-search', status: 'in_progress', title: 'Searching the web', sections: [], defaultExpanded: true },
  found1: { mode: 'web-search', status: 'in_progress', title: 'Searching the web', sections: [SECTION_1], defaultExpanded: true },
  found2: { mode: 'web-search', status: 'in_progress', title: 'Searching the web', sections: [SECTION_1, SECTION_2], defaultExpanded: true },
};

type Point = { x: number; y: number };

export interface FeaturePlaygroundExperimentProps {
  className?: string;
}

/**
 * FeaturePlaygroundExperiment — the prompt box with a looping scripted scene:
 *   1. a cursor comes in and presses web search,
 *   2. the camera zooms into the bar and a query is typed,
 *   3. the cursor presses submit; the camera eases back out and the real search widget runs below the
 *      bar: it searches the web, lists the sources it found, searches again, and stacks the second
 *      set under the first,
 * then it resets and loops. The bar holds a fixed spot near the top (it never re-centres vertically),
 * so only the camera and the growing result list move. This owns no background; the feature section
 * provides the backdrop.
 */
export function FeaturePlaygroundExperiment({
  className,
}: FeaturePlaygroundExperimentProps) {
  const prefersReduced = usePrefersReducedMotion();
  const cameraRef = useRef<HTMLDivElement>(null);
  const [webOn, setWebOn] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [cursor, setCursor] = useState<Point>(REST);
  const [cursorVisible, setCursorVisible] = useState(false);
  const [zoom, setZoom] = useState(1);
  // While the camera is animating we promote it to its own layer; once settled we drop the hint so the
  // browser re-rasterizes the scaled text at full resolution (otherwise it stays soft until a click).
  const [zooming, setZooming] = useState(false);
  const [text, setText] = useState('');
  const [webTarget, setWebTarget] = useState<Point>(REST);
  const [searchPhase, setSearchPhase] = useState<SearchPhase>('idle');

  // Centre of an element in design coordinates. The offsetLeft/offsetTop chain is layout based, so it
  // is unaffected by the camera transform (the zoom). The stack is positioned with px (no transform),
  // so the chain stays accurate.
  const measureCenter = (el: HTMLElement | null): Point | null => {
    const cam = cameraRef.current;
    if (!cam || !el) return null;
    let x = 0;
    let y = 0;
    let node: HTMLElement | null = el;
    while (node && node !== cam) {
      x += node.offsetLeft;
      y += node.offsetTop;
      node = node.offsetParent as HTMLElement | null;
    }
    return { x: x + el.offsetWidth / 2, y: y + el.offsetHeight / 2 };
  };

  // The web button does not move before the cursor presses it (empty bar, no zoom), so measure it once.
  useLayoutEffect(() => {
    const target = measureCenter(
      cameraRef.current?.querySelector<HTMLElement>('[data-playground="web-button"]') ?? null,
    );
    if (target) setWebTarget(target);
  }, []);

  // The scene timeline. Loops until unmount. Reduced motion shows the static idle state.
  useEffect(() => {
    if (prefersReduced) {
      setWebOn(false);
      setZoom(1);
      setText('');
      setCursorVisible(false);
      setCursor(REST);
      setSearchPhase('idle');
      return;
    }
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const at = (fn: () => void, delay: number) => {
      timers.push(setTimeout(() => !cancelled && fn(), delay));
    };
    const typeOut = (full: string, start: number) => {
      for (let i = 1; i <= full.length; i++) {
        at(() => setText(full.slice(0, i)), start + i * CHAR_MS);
      }
      return start + full.length * CHAR_MS;
    };
    const run = () => {
      // Reset to the start: drop the search, clear the bar, turn search off.
      setZoom(1);
      setSearchPhase('idle');
      setPressed(false);
      setCursor(REST);
      setCursorVisible(false);
      at(() => {
        setText('');
        setWebOn(false);
      }, 500);

      // Scene 1: cursor comes in and presses search.
      at(() => setCursorVisible(true), 800);
      at(() => setCursor(webTarget), 950); // move to the web button
      at(() => setPressed(true), 1700); // press down
      at(() => {
        setPressed(false);
        setWebOn(true); // search turns on
      }, 1820);

      // Scene 2: zoom into the bar and type the query.
      at(() => setCursorVisible(false), 2250);
      at(() => setZoom(ZOOM), 2350);
      const typeEnd = typeOut(TYPED_TEXT, 2950);

      // Scene 3: cursor presses submit, the camera eases out, and the search runs below the bar.
      at(() => setCursorVisible(true), typeEnd + 450);
      at(() => {
        const target = measureCenter(
          cameraRef.current?.querySelector<HTMLElement>('[aria-label="Send message"]') ?? null,
        );
        if (target) setCursor(target);
      }, typeEnd + 600); // move to the round submit button
      at(() => setPressed(true), typeEnd + 1350); // press submit
      at(() => setPressed(false), typeEnd + 1470); // submit fires
      at(() => {
        setCursorVisible(false);
        setText(''); // the bar clears, like a real send
        setZoom(1); // ease back out first
      }, typeEnd + 1600);
      at(() => setSearchPhase('searching'), typeEnd + 2350); // after the zoom settles: shimmer
      at(() => setSearchPhase('found1'), typeEnd + 3550); // first search's sources stream in
      at(() => setSearchPhase('found2'), typeEnd + 5250); // a second search stacks its sources below

      at(run, typeEnd + 8200); // hold on the full result, then loop
    };
    run();
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [prefersReduced, webTarget]);

  return (
    <FeatureDemoScaleFit
      designWidth={DESIGN_WIDTH}
      designHeight={DESIGN_HEIGHT}
      className={className}
    >
      <div className={styles.stage} data-name="FeaturePlaygroundExperiment">
        <motion.div
          ref={cameraRef}
          className={styles.camera}
          style={{ willChange: zooming ? 'transform' : 'auto' }}
          initial={false}
          animate={{ scale: zoom }}
          transition={ZOOM_T}
          onAnimationStart={() => setZooming(true)}
          onAnimationComplete={() => setZooming(false)}
        >
          {/* Bar + results are one vertically centred group: with no results the bar sits dead centre;
              as sources arrive the group stays centred and glides (position only, so nothing is
              clipped or stretched) and the whole thing still fits without cropping. */}
          <div className={styles.stack}>
            <motion.div layout="position" className={styles.barWrap} transition={POS_T}>
              <PlaygroundChatBar inputValue={text} webSearchEnabled={webOn} />
            </motion.div>

            {searchPhase !== 'idle' && (
              // Position glide only, no opacity animation. Mixing opacity with framer `layout` left the
              // card stuck half-faded, so the search just appears and glides into place as it grows.
              <motion.div
                layout="position"
                transition={POS_T}
                className={styles.searchSlot}
                data-playground="search-widget"
              >
                {/* Sized to its content (no height animation), so the searching card is compact and the
                    result list is never clipped mid-stretch. */}
                <div className={styles.searchSurface} data-phase={searchPhase}>
                  <ResearchWorkflowWidgetUI {...SEARCH_PROPS[searchPhase]} />
                </div>
              </motion.div>
            )}
          </div>

          {cursorVisible && (
            <motion.div
              className={styles.cursor}
              initial={false}
              animate={{ x: cursor.x, y: cursor.y, scale: pressed ? 0.8 : 1 }}
              transition={{ x: SPRING_MOVE, y: SPRING_MOVE, scale: SPRING_PRESS }}
            >
              <svg width="24" height="32" viewBox="0 0 15 21" fill="none" aria-hidden="true">
                <path
                  d="M1.2 1L1.2 16.5L5.4 12.8L8.1 19.2L10.4 18.2L7.7 12L13 12Z"
                  fill="#1c1c1e"
                  stroke="#ffffff"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.div>
          )}
        </motion.div>
      </div>
    </FeatureDemoScaleFit>
  );
}

FeaturePlaygroundExperiment.displayName = 'FeaturePlaygroundExperiment';
